import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './ProductionRootCause.module.css';
import SourceFileSummary from './SourceFileSummary';
import {
  artavaRechartsAxis,
  artavaRechartsGrid,
  artavaRechartsTooltip,
} from './chartPalette';
import { fetchHotspotAnalysis } from './productionRootCause/productionRootCauseApi';

const defaultFilters = {
  startDate: '',
  endDate: '',
  ateSwVersion: '',
  stationName: '',
  selectedFamilies: [],
};

const groupOptions = [
  { key: 'stations', label: 'Stations' },
  { key: 'operators', label: 'Operators' },
  { key: 'fixtures', label: 'Fixtures' },
  { key: 'versions', label: 'ATE SW' },
  { key: 'hours', label: 'Hours' },
];

const ProductionHotspots = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState({ summary: null, stations: [], operators: [], fixtures: [], versions: [], hours: [] });
  const [selectedGroup, setSelectedGroup] = useState('stations');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeRows = useMemo(() => data[selectedGroup] || [], [data, selectedGroup]);
  const topRows = useMemo(() => activeRows.slice(0, 12), [activeRows]);

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchHotspotAnalysis(nextFilters);
      setData(response || { summary: null, stations: [], operators: [], fixtures: [], versions: [], hours: [] });
    } catch (requestError) {
      setError(requestError.message || 'Failed to load production hotspots.');
      setData({ summary: null, stations: [], operators: [], fixtures: [], versions: [], hours: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  };

  const summary = data.summary || {};

  return (
    <div className={styles.pageContainer}>
      <GoToDashboardButton />

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Root problem idea 3</p>
          <h1 className={styles.title}>Production Hotspots</h1>
          <p className={styles.description}>
            Compare stations, operators, fixtures, software versions, and test hours against the baseline failure rate. The strongest lift often points to calibration, tooling, handling, or shift-specific causes.
          </p>
        </div>
      </header>

      <section className={styles.filterPanel}>
        <div className={styles.filterHeader}>
          <div>
            <h3>Filters</h3>
            <p>Use the same slice before comparing groups; a hotspot is meaningful when its failure lift stays high inside a focused production window.</p>
          </div>
        </div>
        <div className={styles.filterGrid}>
          <div className={styles.filterItem}>
            <label>Start date</label>
            <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>End date</label>
            <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>ATE SW version</label>
            <input value={filters.ateSwVersion} onChange={(event) => updateFilter('ateSwVersion', event.target.value)} placeholder="All versions" />
          </div>
          <div className={styles.filterItem}>
            <label>Station name</label>
            <input value={filters.stationName} onChange={(event) => updateFilter('stationName', event.target.value)} placeholder="All stations" />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.primaryButton} onClick={() => loadData()} disabled={loading}>Run analysis</button>
            <button className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          </div>
        </div>
        <SourceFileSummary
          compact
          startDate={filters.startDate}
          endDate={filters.endDate}
          ateSwVersion={filters.ateSwVersion}
          selectedFamilies={filters.selectedFamilies}
          onFamiliesChange={(values) => updateFilter('selectedFamilies', values)}
          title="Source files used for dashboard charts"
        />
      </section>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metricGrid}>
        <div className={styles.metricCard}><span>Sessions</span><strong>{summary.sessions || 0}</strong></div>
        <div className={styles.metricCard}><span>Failures</span><strong>{summary.failures || 0}</strong></div>
        <div className={styles.metricCard}><span>Baseline fail rate</span><strong>{summary.baselineFailureRate || 0}%</strong></div>
        <div className={styles.metricCard}><span>Station groups</span><strong>{summary.groupsAnalyzed || 0}</strong></div>
      </section>

      <section className={styles.filterPanel}>
        <div className={styles.filterActions}>
          {groupOptions.map((option) => (
            <button
              key={option.key}
              className={selectedGroup === option.key ? styles.primaryButton : styles.secondaryButton}
              onClick={() => setSelectedGroup(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      {loading ? (
        <div className={styles.loading}>Finding production hotspots...</div>
      ) : activeRows.length === 0 ? (
        <div className={styles.emptyState}>No hotspot groups matched this slice.</div>
      ) : (
        <>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Failure lift by group</h3>
                <p>Lift compares each group to the overall baseline. Values above 1.0 deserve investigation when volume is also meaningful.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <BarChart data={topRows} margin={{ top: 10, right: 20, bottom: 60, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="label" angle={-35} textAnchor="end" height={85} {...artavaRechartsAxis} />
                  <YAxis {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar dataKey="lift" name="Failure lift" radius={[6, 6, 0, 0]}>
                    {topRows.map((row) => (
                      <Cell key={row.label} fill={row.lift >= 1.5 ? '#8a3f5d' : '#24745f'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Volume versus failure rate</h3>
                <p>High-right points combine enough production volume with a high failure rate.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 35, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} />
                  <XAxis dataKey="sessions" name="Sessions" {...artavaRechartsAxis} />
                  <YAxis dataKey="failureRate" name="Failure rate" unit="%" {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Scatter data={activeRows} fill="#4f7f99" name="Groups" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.insightGrid}>
            {activeRows.slice(0, 3).map((row) => (
              <article className={styles.insightCard} key={row.label}>
                <p className={styles.eyebrow}>{row.dimension} hotspot</p>
                <h3>{row.label}</h3>
                <p>{row.failureRate}% failure rate across {row.sessions} sessions, {row.lift}x the baseline, with {row.failSteps} failed step rows.</p>
                <div className={styles.chips}>
                  <span>{row.failures} failed sessions</span>
                  <span>{row.affectedSerials} serials</span>
                  <span>{row.avgDurationMinutes} min avg</span>
                </div>
              </article>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Hotspot evidence</h3>
                <p>Use lift for prioritization, but confirm with enough sessions and affected serials before changing the process.</p>
              </div>
            </div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Group</th>
                  <th>Sessions</th>
                  <th>Failures</th>
                  <th>Fail rate</th>
                  <th>Lift</th>
                  <th>Fail steps</th>
                  <th>Serials</th>
                  <th>Avg duration</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {activeRows.map((row, index) => (
                  <tr key={row.label}>
                    <td>{index + 1}</td>
                    <td className={styles.stepName}>{row.label}<br /><span className={styles.muted}>{row.dimension}</span></td>
                    <td>{row.sessions}</td>
                    <td>{row.failures}</td>
                    <td>{row.failureRate}%</td>
                    <td>{row.lift}x</td>
                    <td>{row.failSteps}</td>
                    <td>{row.affectedSerials}</td>
                    <td>{row.avgDurationMinutes} min</td>
                    <td>{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default ProductionHotspots;
